import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Autocomplete,
  TextField,
  Tooltip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import { reports, reportCategories } from "../config/reportRegistry";

const Reports = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");

  const visibleReports = useMemo(() => {
    return reports.filter(report =>
      report.roles.includes(user?.role)
    );
  }, [user]);

  const filteredReports = useMemo(() => {
    if (!searchTerm) return [];

    return visibleReports.filter(report =>
      report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.tags.some(tag =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, visibleReports]);

  const groupedReports = useMemo(() => {
    return reportCategories.reduce((acc, category) => {
      acc[category] = visibleReports.filter(
        report => report.category === category
      );
      return acc;
    }, {});
  }, [visibleReports]);

  const renderButton = (report) => {
    if (report.status === "inactive") {
      return (
        <Tooltip title="This report is not available yet">
          <span>
            <Button
              variant="outlined"
              size="small"
              disabled
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Coming Soon
            </Button>
          </span>
        </Tooltip>
      );
    }

    return (
      <Button
        variant="contained"
        size="small"
        onClick={() => navigate(report.route)}
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        Open Report
      </Button>
    );
  };

  const renderStatusChip = (report) => {
    return (
      <Chip
        label={report.status === "active" ? "Active" : "Inactive"}
        size="small"
        color={report.status === "active" ? "success" : "default"}
        sx={{ fontWeight: 600 }}
      />
    );
  };

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        Reports Center
      </Typography>

      <Autocomplete
        freeSolo
        options={visibleReports}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.name
        }
        sx={{ mb: 4, maxWidth: 500 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Reports"
            placeholder="Type keywords like time, money, budget..."
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        )}
      />

      {searchTerm && (
        <Box sx={{ mb: 6 }}>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
            Search Results
          </Typography>

          {filteredReports.length === 0 ? (
            <Typography color="text.secondary">
              No reports found.
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {filteredReports.map(report => (
                <Grid item xs={12} sm={6} md={4} key={report.id}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography fontWeight="bold">
                        {report.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ mb: 2, color: "#6b7280" }}
                      >
                        {report.description}
                      </Typography>

                      <Box sx={{display:"flex",gap:1,mb:2}}>
                        <Chip
                          label={report.category}
                          size="small"
                        />
                        {renderStatusChip(report)}
                      </Box>

                      {renderButton(report)}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {!searchTerm &&
        reportCategories.map(category => {
          const categoryReports = groupedReports[category];
          if (!categoryReports || categoryReports.length === 0)
            return null;

          return (
            <Box key={category} sx={{ mb: 5 }}>
              <Typography
                variant="h6"
                fontWeight="600"
                sx={{ mb: 2, color: "#374151" }}
              >
                {category}
              </Typography>

              <Grid container spacing={3}>
                {categoryReports.map(report => (
                  <Grid item xs={12} sm={6} md={4} key={report.id}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        border: "1px solid #e5e7eb",
                        "&:hover": {
                          boxShadow:
                            "0 6px 18px rgba(0,0,0,0.08)",
                          transform: "translateY(-3px)"
                        }
                      }}
                    >
                      <CardContent>

                        <Typography fontWeight="bold">
                          {report.name}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ mb: 2, color: "#6b7280" }}
                        >
                          {report.description}
                        </Typography>

                        <Box sx={{display:"flex",gap:1,mb:2}}>
                          <Chip
                            label={report.format.toUpperCase()}
                            size="small"
                          />
                          {renderStatusChip(report)}
                        </Box>

                        {renderButton(report)}

                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}
    </Box>
  );
};

export default Reports;